import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatChipInputEvent } from '@angular/material/chips';
import { FormControl } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { DateTime } from '@lib/date-time';
import { Device } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { emailFormatValidator } from '@app/utilities/device-alert';

@Component({
    selector: 'device-offline-alert',
    templateUrl: './device-offline-alert.component.html',
    styleUrls: ['./device-offline-alert.component.scss']
})
export class DeviceOfflineAlertComponent implements OnInit, OnDestroy {
    constructor(
        private readonly webClient: WebClient,
        private readonly fragQuery: FragQueryService,
        private readonly dialog: DialogService,
        private readonly snackBar: MatSnackBar
    ) { }

    private readonly destroy$ = new Subject<void>();

    public device: Device | null = null;
    public emailFormControl = new FormControl('', [emailFormatValidator]);
    public alertDuration = new DateTime(new DateTime().setHours(0, 0, 0, 0));
    public alertEmails: string[] = [];
    public readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly deviceUuid$ = this.fragQuery.deviceUuid$;

    ngOnInit(): void {
        this.device$
            .pipe(takeUntil(this.destroy$))
            .subscribe(device => this.loadOfflineAlertSettings(device));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadOfflineAlertSettings(device: Device | null): void {
        if (!device) return;

        this.device = device;
        const config = device.offlineAlert ? JSON.parse(device.offlineAlert) : null;

        this.alertDuration = new DateTime(
            new DateTime().setHours(
                config?.duration?.hours ?? 0,
                config?.duration?.minutes ?? 0,
                0,
                0
            )
        );
        const emailString = config?.email ?? '';
        if (emailString) {
            this.alertEmails = emailString.split(',').map((e: string) => e.trim()).filter((e: string) => e);
        } else {
            this.alertEmails = [];
        }
    }

    public onDurationChange(interval: DateTime): void {
        this.alertDuration = interval;
    }

    public async saveSettings(): Promise<void> {
        if (!this.device) {
            this.snackBar.open('No device selected', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-success'],
            });
            return;
        }

        if (this.emailFormControl.invalid) {
            this.snackBar.open('Cannot save, invalid email format.', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-success'],
            });
            return;
        }

        const offlineAlert = {
            duration: {
                hours: this.alertDuration.getHours(),
                minutes: this.alertDuration.getMinutes()
            },
            email: this.alertEmails.join(',')
        };

        this.device.offlineAlert = JSON.stringify(offlineAlert);

        await this.dialog.await(this.webClient.model.device.save(this.device));

        this.snackBar.open('Offline alert settings saved', 'OK', {
            duration: 5000,
            panelClass: ['snackbar-success'],
        });

        this.fragQuery.trigger$.next();
    }

    //add email on the enter or (,) click
    public addEmail(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        if (!value) return;

        // Reset the form control before validating the new input
        this.emailFormControl.setValue(value);
        this.emailFormControl.markAsDirty();

        if (this.emailFormControl.valid && !this.alertEmails.includes(value) && value) {
            this.alertEmails.push(value);
            event.chipInput!.clear();
            this.emailFormControl.reset(); // Reset form control after adding valid email
        } else if (value.trim()) {
            this.snackBar.open('Invalid email format', 'OK', {
                duration: 3000,
                panelClass: ['snackbar-success'],
            });
        }
    }

    public removeEmail(email: string): void {
        const index = this.alertEmails.indexOf(email);
        if (index >= 0) {
            this.alertEmails.splice(index, 1);
        }
    }

    public valid() {
        if (this.alertDuration.getHours() === 0 && this.alertDuration.getMinutes() === 0) return false;
        const pendingEmail = this.emailFormControl.value?.trim();
        if (pendingEmail) {
            if (!this.emailFormControl.valid) {
                return false;
            }
        } else if (this.alertEmails.length === 0) {
            return false;
        }

        return true;
    }
}